"use client";
import { useEffect, useState } from "react";
import {
  signInWithGoogle,
  signInAnonymous,
  signInWithMicrosoft,
  signOutFirebase,
  signInWithEmail,
  signInWithSaml,
  sendEmailSignInLink,
  isEmailSignInLink,
  completeEmailLinkSignIn,
  rememberEmailForSignIn
} from "../../helpers/firebase";
import { sendEmailLinkService } from "../../services/sendEmailLinkService";
import { useUser } from "../../context/UserContext";
import { ChevronDown, ChevronLeft, CircleUserRound, Mail } from "lucide-react";
import { Constantes } from "@/app/constants/constantsGlobal";
import { AccessTypeForm, AlertType } from "@/app/constants/enums";
import Modal from "../Elements/Modal";

const GoogleLogin = ({ isModalOpen, closeModal }: { isModalOpen: boolean; closeModal: () => void }) => {
  const [loading, setLoading] = useState({ google: false, microsoft: false, anonymous: false, email: false , azure:false, generic:false });
  const [loadingGeneric, setLoadingGeneric] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {user, userProject, setUser, userProjectLoaded, userProjectLoading, addAlert} = useUser();


  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loginModeForm, setLoginModeForm] = useState<AccessTypeForm>(AccessTypeForm.Providers);

  // acceso por enlace al correo (passwordless)
  const [showEmailLink, setShowEmailLink] = useState(false);
  const [emailLink, setEmailLink] = useState("");
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  // El componente queda montado aunque el modal se cierre (solo se oculta),
  // así que hay que reiniciar el formulario de enlace al reabrirlo: si no,
  // tras cerrar sesión seguiría mostrando "enviamos un enlace a...".
  useEffect(() => {
    if (isModalOpen) {
      setShowEmailLink(false);
      setEmailLinkSent(false);
      setSendingLink(false);
      setEmailLink("");
    }
  }, [isModalOpen]);

  // Completa el ingreso cuando el usuario vuelve desde el enlace del correo
  useEffect(() => {
    if (typeof window === "undefined" || !isEmailSignInLink(window.location.href)) return;
    const link = window.location.href;
    // Limpia los parámetros del enlace (oobCode, mode, apiKey) de la barra de
    // direcciones: una recarga o logout volvería a disparar este flujo.
    const params = new URLSearchParams(window.location.search);
    ["mode", "oobCode", "apiKey", "continueUrl", "lang", "tenantId"].forEach((p) => params.delete(p));
    const query = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash);

    completeEmailLinkSignIn(link)
      .then((userData) => {
        if (userData) {
          setUser(userData);
          closeModal();
        }
      })
      .catch((err: any) => {
        if (err?.code === "auth/invalid-action-code") {
          addAlert("El enlace ya fue usado o expiró. Solicita uno nuevo.", AlertType.Error);
        } else if (err?.code === "auth/invalid-email") {
          addAlert("El correo no coincide con el enlace recibido.", AlertType.Error);
        } else {
          addAlert("No se pudo completar el ingreso con el enlace.", AlertType.Error);
        }
      });
  }, []);

  // Envía el enlace de acceso sin contraseña al correo indicado.
  // Lo genera y envía el backend (Admin SDK + correo corporativo) para que
  // pase los filtros de los tenants Microsoft; si el endpoint no está
  // disponible, cae al envío directo de Firebase.
  const handleSendEmailLink = async () => {
    const emailValue = (emailLink || "").trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      addAlert("Ingresa un correo válido.", AlertType.Warning);
      return;
    }
    setEmailLink(emailValue);
    setSendingLink(true);
    try {
      const res = await sendEmailLinkService(emailValue, window.location.href);
      if (res?.success) {
        rememberEmailForSignIn(emailValue);
        setEmailLinkSent(true);
      } else {
        addAlert(res?.message || "No se pudo enviar el enlace. Intenta nuevamente.", AlertType.Error);
      }
      setSendingLink(false);
    } catch (err) {
      console.warn("email_link/send no disponible, usando envío directo de Firebase", err);
      await sendEmailLinkWithFirebase(emailValue);
    }
  };

  const sendEmailLinkWithFirebase = async (emailValue: string) => {
    try {
      await sendEmailSignInLink(emailValue);
      setEmailLinkSent(true);
    } catch (err: any) {
      if (err?.code === "auth/operation-not-allowed") {
        addAlert("El acceso por enlace no está habilitado. Contacta al administrador.", AlertType.Error);
      } else if (err?.code === "auth/unauthorized-continue-uri") {
        addAlert("El dominio de la aplicación no está autorizado para el enlace de acceso.", AlertType.Error);
      } else {
        addAlert("No se pudo enviar el enlace. Intenta nuevamente.", AlertType.Error);
      }
    } finally {
      setSendingLink(false);
    }
  };


  const handleLogin = async (provider: "google" | "microsoft" | "azure" | "anonymous"| "email"| "generic", loginFunction: () => Promise<any>) => {
    setLoading(prev => ({ ...prev, [provider]: true }));
    try { 
      const userData = await loginFunction();
      setUser(userData);      
      closeModal();
    } catch (err) {
      setError("Error al iniciar sesión");
    } finally { 
      setLoading(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleLogout = async () => {
    await signOutFirebase();
    setUser(null); 
    closeModal();
  };

  const handleLogInAnonymous = async () => {
    await signOutFirebase();
    setUser(null);
    closeModal();
  };


  const [options, setOptions] = useState<{ id: string; label: string }[]>([
      { id: 'otros', label: "Otras Empresas del Grupo Intercorp " },
      { id: 'casa-andina', label: 'Casa Andina' },
      { id: 'foh', label: 'Financiera OH' },
      { id: 'smi', label: 'SMI' },
      //{ value: 1, label: "In Learning" }, // Corregido "in Lerning" -> "In Learning"
    ]);
  const [selectValue, setSelectValue] = useState<string>('otros');
 


  return (
    <div> 
      <Modal
          isOpen={isModalOpen}
          onClose={() => {}}
          btnClose={false}
        >
        <div className="bg-white rounded-lg shadow-lg text-center n-card-loggin relative animate-fadeIn">
        <div className="relative "> 
            <button
              onClick={() => setLoginModeForm(AccessTypeForm.Providers)}
              className={`absolute n-buttons shadow-md -top-5 -left-3 flex items-center justify-center w-10 h-10 rounded-full transition-all
                ${loginModeForm === AccessTypeForm.Providers ? "opacity-0 pointer-events-none" : "opacity-100"}
              `} 
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button> 
          </div> 

          <div className="icono-loggin flex justify-center items-center">
            <CircleUserRound strokeWidth={0.3} size={200} />
          </div>

          { loginModeForm===AccessTypeForm.Providers&&(
            <div className="relative mb-3">
              <select
                name="typeLoagin"
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                className="appearance-none w-full border n-fmr-input px-4 pr-8 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
              >
                {options.map((option) => (
                  <option key={`${option.id}`} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-5 h-5 absolute inset-y-0 top-3 right-3 pointer-events-none text-gray-400" />
            </div>
          )} 
            {/* Ingreso con Email y Contraseña */}
            {loginModeForm===AccessTypeForm.User&&(!Constantes.PRODUCTION)&&(
              <div 
                className="animate-fadeIn transition-all duration-300 flex flex-col w-80 items-center gap-2"
              >
                
                  <input
                    type="email"
                    placeholder="Correo electrónico"
                    className="w-full border p-2 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                
                
                  <input
                    type="password"
                    placeholder="Contraseña"
                    className="w-full border p-2 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                
                  <button
                    className="w-full py-3 mb-2 hover:bg-blue-50 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("email", () => signInWithEmail(email, password))}
                    disabled={loading.email}
                  >
                    {loading.email ? "Cargando..." : "Iniciar sesión"}
                  </button>
                  {/*
                  <button
                    className="py-2 bg-gray-500 text-white rounded"
                    onClick={() => signUpWithEmail(email, password)}
                  >
                    Registrarse
                  </button>
                  */}
                  </div>
              
            )}
            
          {loginModeForm===AccessTypeForm.Providers&&(
            <div className="animate-fadeIn transition-all duration-300">
              
              {selectValue=='otros'&&(
                <button
                  type="button"
                  className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                  onClick={() => handleLogin("google", signInWithGoogle)}
                  disabled={loading.google}
                >
                {loading.google ? "Cargando..." : "Iniciar sesión con Google"}
                </button>
              )}
              {selectValue=='foh'&&(
                <>
                <button
                    type="button"
                    className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("google", signInWithGoogle)}
                    disabled={loading.google}
                  >
                    {loading.google ? "Cargando..." : "Iniciar sesión con Google"}
                  </button>
                  
                {
                  <button
                  type="button"
                  className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                  onClick={() => handleLogin("azure", () => signInWithSaml("saml.azure-ad-foh"))}
                  disabled={loading.azure}
                >
                {loading.azure ? "Cargando..." : "Ingresar con Microsoft - Financiera Oh"}
                </button>}
                </>
              )}
              {selectValue=='casa-andina'&&(
                <>
                  <button
                    type="button"
                    className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("google", signInWithGoogle)}
                    disabled={loading.google}
                  >
                    {loading.google ? "Cargando..." : "Iniciar sesión con Google"}
                  </button>
                  <button
                    type="button"
                    className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("microsoft", signInWithMicrosoft)}
                    disabled={loading.microsoft}
                  >
                    {loading.microsoft ? "Cargando..." : "Iniciar sesión con Microsoft 365"}
                  </button>
                </>
              )}
              {selectValue=='smi'&&(
                <>
                  <button
                    type="button"
                    className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("google", signInWithGoogle)}
                    disabled={loading.google}
                  >
                    {loading.google ? "Cargando..." : "Iniciar sesión con Google"}
                  </button>
                  <button
                    type="button"
                    className="w-full py-3 mb-2 hover:bg-blue-600 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                    onClick={() => handleLogin("microsoft", signInWithMicrosoft)}
                    disabled={loading.microsoft}
                  >
                    {loading.microsoft ? "Cargando..." : "Iniciar sesión con Microsoft 365"}
                  </button>
                </>
              )}

              
              {(!Constantes.PRODUCTION)&&(
                <button
                  type="button"
                  className="w-full py-3 mb-2 hover:bg-blue-50 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                  onClick={() => setLoginModeForm(AccessTypeForm.User)} 
                >
                  Iniciar sesión con Usuario
                </button> 
              )}

              {userProjectLoaded && userProject && (!userProject.project.generic_access) ? (
                  <button
                    type="button"
                    className="w-full py-3 mb-2 rounded-lg shadow-lg flex items-center bg-gray-300 hover:bg-gray-200 justify-center btn-login-border"
                    onClick={() => handleLogin("anonymous", signInAnonymous)}
                    disabled={loading.anonymous}
                    >
                    {loading.anonymous ? "Cargando..." : "Iniciar sesión anónimo"}
                  </button> 
                ):(!userProject && 
                <button
                  type="button"
                  className="w-full py-3 mb-2 rounded-lg shadow-lg flex items-center bg-gray-300 hover:bg-gray-200 justify-center btn-login-border"
                  onClick={() => handleLogin("anonymous", signInAnonymous)}
                  disabled={loading.anonymous}
                  >
                  {loading.anonymous ? "Cargando..." : "Iniciar sesión anónimo"}
                </button>
              )}

              {/* acceso sin contraseña por enlace al correo */}
              <button
                type="button"
                className="w-full py-2 mb-1 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                onClick={() => setShowEmailLink(!showEmailLink)}
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                Ingresar con enlace al correo
              </button>
              {showEmailLink && (
                !emailLinkSent ? (
                  <div className="animate-fadeIn flex flex-col gap-2">
                    <input
                      type="email"
                      placeholder="Correo corporativo"
                      className="w-full border p-2 n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-700"
                      value={emailLink}
                      onChange={(e) => setEmailLink(e.target.value)}
                      onKeyUp={(e) => e.key === "Enter" && handleSendEmailLink()}
                    />
                    <button
                      type="button"
                      className="w-full py-3 mb-2 hover:bg-blue-50 rounded-lg shadow-lg flex items-center justify-center n-buttons btn-login-border"
                      onClick={handleSendEmailLink}
                      disabled={sendingLink}
                    >
                      {sendingLink ? "Enviando..." : "Enviarme enlace de acceso"}
                    </button>
                  </div>
                ) : (
                  <div className="animate-fadeIn text-left text-sm bg-green-100 text-green-700 border border-green-200 rounded-lg p-3 mb-2">
                    Enviamos un enlace de acceso a <b>{emailLink}</b>. Revisa tu bandeja (y la
                    carpeta de spam) y ábrelo en este mismo navegador. El enlace es de un solo
                    uso y expira pronto.
                  </div>
                )
              )}

              {/*
              <button
                type="button"
                className="w-full py-3 mb-2 rounded-lg shadow-lg flex items-center bg-gray-300 hover:bg-gray-200 justify-center btn-login-border"
                onClick={() => handleLogin("generic", signInAnonymous)}
                disabled={loading.generic}
              >
                Acceso genérico
              </button>
              
              <button
              type="button"
              className="w-full py-3 mt-4 rounded-lg shadow-lg flex items-center justify-center btn-danger btn-login-border"
              onClick={handleLogout}
              >
              Cerrar sesión
              </button>*/}
            </div>
          )}
          

          
        </div>
      </Modal>
    </div>
  );
};

export default GoogleLogin;
